export const agenticDevEN = {
  seo: {
    title: "Intent-Driven Development (IDD) for Agentic Coding | pAIoneer Labs GmbH",
    description: "Intent-Driven Development makes AI-assisted software development controllable: meaning, decisions, and scenarios become operational artifacts for agentic coding.",
    keywords: "Intent-Driven Development, Intent Driven Development, IDD, Agentic Coding, agentic software development, AI-assisted software development, Vibe Coding, Spec-Driven Development, Prompt Engineering, AI requirements engineering",
    ogTitle: "Intent-Driven Development: meaning before implementation",
    ogDescription: "IDD operationalizes context before code is written: Intent, Decisions, and Scenarios become the project memory for agentic software development.",
    twitterTitle: "Intent-Driven Development (IDD) for Agentic Coding",
    twitterDescription: "Controllable code instead of prompt chaos: IDD turns meaning, decisions, and scenarios into operational artifacts.",
    url: "https://paioneer-labs.eu/agenticdev",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Intent-Driven Development (IDD) for Agentic Coding",
      serviceType: "Agentic software development and AI-assisted development processes",
      description: "Consulting and implementation for Intent-Driven Development, IDD Vaults, agentic coding workflows, and verifiable AI-assisted software development.",
      provider: {
        "@type": "Organization",
        name: "pAIoneer Labs GmbH",
        url: "https://paioneer-labs.eu"
      },
      areaServed: "DE, NL, EU",
      url: "https://paioneer-labs.eu/agenticdev"
    }
  },
  hero: {
    eyebrow: "Agentic coding needs explicit context",
    title: "Intent-Driven Development",
    claim: "Meaning before implementation.",
    lead: "AI writes code. IDD makes sure it works on the right problem. Intent-Driven Development turns goals, boundaries, decisions, and scenarios into operational artifacts for agentic software development."
  },
  implicitContextProblem: {
    title: "The risk is implicit context",
    intro: "The real risk in AI-assisted software development is not missing code. The risk is context hidden in someone's head, inside a chat history, or across scattered tickets.",
    items: [
      {
        title: "Unclear assignment",
        description: "Prompts often describe a solution, but not the goal, non-goals, success criteria, and assumptions. AI then works quickly, but not necessarily in the right direction."
      },
      {
        title: "Accidental architecture",
        description: "Technology decisions happen on the side: persistence, model choice, failure modes, and operations are decided implicitly instead of being led deliberately."
      },
      {
        title: "Unverifiable result",
        description: "Without scenarios and acceptance criteria, it remains unclear whether the code actually behaves correctly or only looks plausible."
      }
    ],
    resolution: "IDD solves exactly this problem: meaning is operationalized first, and code is derived from it afterwards."
  },
  antiVibeCoding: {
    title: "Vibe Coding is not a development process",
    description: "Instinct can be enough for demos. It is not enough for software with operations, maintenance, security, cost, and accountability. Agents do not need more vibes; they need explicit context.",
    contrasts: [
      "Vibe Coding creates movement. IDD creates direction.",
      "Prompting creates answers. IDD creates project memory.",
      "Agentic Coding without IDD remains activity. Agentic Coding with IDD becomes controllable development."
    ]
  },
  principle: {
    title: "Documentation is not a byproduct",
    thesis: "In IDD, documentation is an operational artifact.",
    description: "Documentation is not reporting. It is the workspace where goals, boundaries, decisions, and expected behavior become explicit before agents implement.",
    pillarsTitle: "Intent. Decisions. Scenarios. Code.",
    pillarsIntro: "IDD does not reduce software development to prompts. It makes the meaning of a project explicit and derives verifiable implementation from it.",
    pillars: [
      {
        title: "Intent",
        description: "What should be achieved, what is explicitly out of scope, and how do we recognize success?"
      },
      {
        title: "Decisions",
        description: "Which important decisions were made, in what context, and with which consequences?"
      },
      {
        title: "Scenarios",
        description: "Which behavior is expected in normal cases, edge cases, failure modes, and acceptance situations?"
      },
      {
        title: "Code",
        description: "Implementation is produced from documented meaning and checked against scenarios and verification steps."
      }
    ]
  },
  vault: {
    title: "The IDD Vault: project memory instead of prompt history",
    description: "The IDD Vault keeps relevant context where agents and humans can reuse it. No more files than necessary. Every file has a clear function.",
    rule: "Few artifacts. Clear purpose. Durable context.",
    effect: "The Vault prevents decisive context from disappearing inside chat, inside someone's head, or across scattered tickets.",
    treeLines: [
      "/project",
      "  /idd_vault",
      "    intent.md       # goal, non-goals, success criteria, assumptions",
      "    decisions.md    # decisions with context, rationale, consequences",
      "    scenarios.md    # behavior, edge cases, acceptance criteria",
      "  /code",
      "  AGENTS.md         # rules and expectations for code agents",
      "  README.md         # entry point, setup, operating mode, boundaries"
    ]
  },
  outputs: {
    title: "Not text. Controllable code.",
    description: "The IDD files are not a report. They are input for concrete implementation work and for feeding discoveries back into the project.",
    items: [
      {
        file: "intent.md",
        result: "Scope",
        description: "Endpoints, data model, success criteria, non-goals, and assumptions become visible as the development frame."
      },
      {
        file: "decisions.md",
        result: "Architecture",
        description: "Technology, operations, persistence, model choice, and trade-offs are justified instead of decided on the side."
      },
      {
        file: "scenarios.md",
        result: "Tests",
        description: "Acceptance tests, failure modes, state transitions, and edge cases emerge before or during implementation."
      },
      {
        file: "AGENTS.md",
        result: "Code quality",
        description: "Rules, patterns, constraints, migrations, OpenAPI, and expected outputs become explicit for agents."
      },
      {
        file: "README.md",
        result: "Entry point",
        description: "Setup, operating mode, boundaries, and example flows remain understandable for humans and agents."
      },
      {
        file: "Code",
        result: "Feedback loop",
        description: "Findings from implementation and verification flow back into the Vault, tests, and next iteration."
      }
    ]
  },
  comparison: {
    title: "IDD starts before the specification",
    intro: "Spec-Driven Development is strong when the goal is already technically precise and stable. IDD starts earlier: where domain, assumptions, trade-offs, and failure modes still need to be made explicit.",
    columns: ["Approach", "Center", "Strength", "Limit"],
    rows: [
      {
        approach: "Prompting",
        center: "Input to a model",
        strength: "Fast and direct",
        limit: "Transient, incomplete, and hard to verify"
      },
      {
        approach: "Vibe Coding",
        center: "Instinct and iteration",
        strength: "Useful for playgrounds and demos",
        limit: "No reliable control for accountable software"
      },
      {
        approach: "Spec-Driven Development",
        center: "Technical contract",
        strength: "Consistency against an already defined specification",
        limit: "The wrong problem can be implemented formally correctly"
      },
      {
        approach: "Intent-Driven Development",
        center: "Meaning, goal, and decisions",
        strength: "Clarifies context, trade-offs, and boundaries before implementation",
        limit: "Requires disciplined context maintenance and validation"
      }
    ],
    summary: "Specs say what must be technically true. IDD explains why exactly this direction should be built."
  },
  workflow: {
    title: "Clarify. Decide. Formulate. Build. Feed back.",
    description: "IDD is not waterfall. IDD is small, controlled iteration with durable context.",
    steps: [
      {
        title: "Clarify intent",
        description: "Goals, non-goals, success criteria, and assumptions become visible before code is written."
      },
      {
        title: "Document decisions",
        description: "Architecture and product decisions are captured with context, rationale, and consequences."
      },
      {
        title: "Formulate scenarios",
        description: "Normal behavior, edge cases, failure modes, and acceptance criteria are described in verifiable form."
      },
      {
        title: "Let agents implement",
        description: "AI agents work from explicit context instead of isolated prompts."
      },
      {
        title: "Verify and feed back",
        description: "Findings from code, tests, and operations update the Vault and the next iteration."
      }
    ]
  },
  impact: {
    title: "Why this matters in real projects",
    description: "IDD makes AI-assisted development connectable to teams, operations, and quality assurance.",
    items: [
      "Less rework because goals and non-goals are visible.",
      "Better architecture because decisions are justified.",
      "Better tests because scenarios exist before code.",
      "Better collaboration because context does not disappear inside chat.",
      "Better agent results because every step is based on explicit artifacts."
    ]
  },
  contact: {
    title: "Bring IDD into agentic development processes",
    description: "If AI-assisted development should become more than experimentation, it needs an operational context model.",
    details: "We help build IDD Vaults, agent rules, scenarios, and verifiable development workflows.",
    button: "Discuss IDD",
    subject: "Discuss Intent-Driven Development",
    email: "info@paioneer.eu"
  }
};
